import { config } from "../config";
import { useAuthStore } from "../stores/auth";

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiCallError extends Error {
  constructor(
    public status: number,
    public error: ApiError,
  ) {
    super(error.message);
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  auth?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${config.apiBaseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return false;
      }
      const data = (await res.json()) as { ok: boolean; data: { access_token: string; refresh_token: string } };
      if (!data.ok) return false;
      useAuthStore.getState().setTokens(data.data.access_token, data.data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set("accept", "application/json");
  if (opts.body !== undefined) headers.set("content-type", "application/json");

  const useAuth = opts.auth ?? true;
  if (useAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set("authorization", `Bearer ${token}`);
  }

  const init: RequestInit = {
    ...opts,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "omit",
  };

  let res = await fetch(`${config.apiBaseUrl}${path}`, init);

  if (res.status === 401 && useAuth) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      const token = useAuthStore.getState().accessToken;
      if (token) headers.set("authorization", `Bearer ${token}`);
      res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
    }
  }

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as { ok: boolean; data?: T; error?: ApiError }) : null;
  if (!res.ok || !parsed || !parsed.ok) {
    throw new ApiCallError(
      res.status,
      parsed?.error ?? { code: "network_error", message: `HTTP ${res.status}` },
    );
  }
  return parsed.data as T;
}
